<template>
  <div class="contractManagement">
    <!-- 付款凭证审核失败等提示 -->
    <div
      v-for="(alert, index) in errorAlertList"
      :key="index"
      class="contractManagementErrorBanner"
    >
      <p class="contractManagementErrorText" v-html="alert.message"></p>
    </div>

    <h2 class="contractManagementTitle">协议信息</h2>

    <el-table
      class="contractManagementTable"
      :data="contractList"
      border
      v-loading="tableLoading"
      empty-text="暂无协议数据"
    >
      <el-table-column
        prop="contractNo"
        label="协议编号"
        min-width="100"
        align="center"
      />
      <el-table-column
        prop="authType"
        label="授权类型"
        min-width="80"
        align="center"
      />
      <el-table-column
        prop="authModeText"
        label="授权模式"
        min-width="100"
        align="center"
      />
      <el-table-column
        prop="contractType"
        label="协议类型"
        min-width="100"
        align="center"
      />
      <el-table-column
        prop="signDate"
        label="协议签订日期"
        min-width="120"
        align="center"
      />
      <el-table-column label="协议到期日期" min-width="140" align="center">
        <template slot-scope="scope">
          <div>{{ scope.row.expireDate }}</div>
          <div v-if="scope.row.expireSoon" class="contractManagementExpireWarn">
            {{ scope.row.expireSoonText }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        min-width="160"
        align="center"
        fixed="right"
      >
        <template slot-scope="scope">
          <a
            v-if="scope.row.canRenew"
            class="contractManagementAction"
            href="javascript:;"
            @click.prevent="onRenewContract(scope.row)"
          >
            续签协议
          </a>
          <a
            class="contractManagementAction"
            :class="{ 'contractManagementAction--solo': !scope.row.canRenew }"
            href="javascript:;"
            @click.prevent="onViewContract(scope.row)"
          >
            查看协议
          </a>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { getContractList } from "@/api/lx";

/**
 * 合同管理模块
 * 对应设计稿：付款凭证提示、合同信息表格
 */
export default {
  name: "ContractManagement",
  props: {
    /** 父组件传入的企业信息 */
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tableLoading: false,
      /** 顶部错误/警告横幅列表 */
      errorAlertList: [],
      /** 合同列表 */
      contractList: [],
    };
  },
  watch: {
    company: {
      handler() {
        this.fetchContractList();
      },
      deep: true,
    },
  },
  created() {
    this.fetchContractList();
  },
  methods: {
    /**
     * 格式化到期时间
     */
    formatExpireDate(endTime) {
      if (!endTime) return "--";
      const time = new Date(String(endTime).replace(/-/g, "/"));
      if (isNaN(time.getTime())) return endTime;
      const year = time.getFullYear();
      const month = String(time.getMonth() + 1).padStart(2, "0");
      const date = String(time.getDate()).padStart(2, "0");
      return `${year}-${month}-${date}`;
    },

    /**
     * 计算到期时间
     */
    getDaysUntilExpire(endTime) {
      if (!endTime) return null;
      const end = new Date(String(endTime).replace(/-/g, "/"));
      if (isNaN(end.getTime())) return null;
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return Math.ceil((end - now) / (24 * 60 * 60 * 1000));
    },

    /**
     * 拉取合同列表
     */
    fetchContractList() {
      this.tableLoading = true;
      getContractList()
        .then((res) => {
          const list = (res && res.data) || [];
          this.errorAlertList = []; // 接口若有警告提示可通过扩展此字段展示

          this.contractList = list.map((item) => {
            const days = this.getDaysUntilExpire(item.endTime);
            const authLevelMap = {
              0: "L1",
              1: "L2",
              2: "L3",
              3: "L4",
            };
            const authType =
              authLevelMap[item.contractLevel] || item.contractLevel || "--";

            const authModeMap = {
              0: "定额永许",
              1: "会员无限",
            };
            const authModeText = authModeMap[item.authMode] || "--";

            // 合同类型判定，优先级：授权合同 > 补充合同 > 三方合同
            let contractType = "--";
            if (item.authContractFile) {
              contractType = "授权协议";
            } else if (item.otherContractFile) {
              contractType = "补充协议";
            } else if (item.thirdPartyContractFile) {
              contractType = "三方协议";
            }

            const expireSoon = days !== null && days <= 30;
            const expireSoonText = expireSoon
              ? days < 0
                ? "协议已到期"
                : `${days}天后协议到期`
              : "";

            // 30天内（含30天）为即将到期，或状态为已到期/休眠（authStatus===2）则允许续签
            const canRenew =
              (days !== null && days <= 30) || item.authStatus === 2;

            return {
              ...item,
              contractNo: item.contractCode || "--",
              authType: authType,
              authModeText: authModeText,
              contractType: contractType,
              signDate: this.formatExpireDate(item.startTime),
              expireDate: this.formatExpireDate(item.endTime),
              expireSoon: expireSoon,
              expireSoonText: expireSoonText,
              canRenew: canRenew,
            };
          });
        })
        .catch((err) => {
          console.error("加载协议列表失败:", err);
          this.$message.error("加载协议列表失败");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },

    /** 续签协议 */
    onRenewContract(row) {
      this.$emit("renew-contract", row);
    },

    /** 查看协议 */
    onViewContract(row) {
      let url =
        row.authContractFile ||
        row.otherContractFile ||
        row.thirdPartyContractFile;
      if (!url) {
        this.$message.warning("暂无协议文件");
        return;
      }
      // 处理可能存在逗号分隔的多个文件或 JSON 数组包裹的情况，提取出第一个文件的真实 URL
      if (typeof url === "string") {
        url = url.trim();
        if (url.startsWith("[") && url.endsWith("]")) {
          try {
            const arr = JSON.parse(url);
            if (Array.isArray(arr) && arr.length) {
              url = arr[0];
            }
          } catch (e) {
            url = url
              .replace(/[\[\]"']/g, "")
              .split(",")[0]
              .trim();
          }
        } else if (url.includes(",")) {
          url = url.split(",")[0].trim();
        }
      }
      if (!url) {
        this.$message.warning("暂无协议文件");
        return;
      }
      // 使用正则检查 URL 路径中或参数中是否包含 .pdf（支持含 query 参数如 filename=xxx.pdf）
      if (/\.pdf(\?|&|#|$)/i.test(url)) {
        this.$router.push({
          path: "/pdf/index",
          query: { url, name: row.contractNo || "协议" },
        });
      } else {
        window.open(url, "_blank");
      }
    },
  },
};
</script>

<style lang="less" scoped>
.contractManagement {
  width: 100%;
}
.contractManagementErrorBanner {
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 2px;
  padding: 12px;
  margin-bottom: 32px;
  text-align: center;
}
.contractManagementErrorText {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #f5222d;
}
.contractManagementTitle {
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 700;
  color: #00161a;
}
.contractManagementTable {
  width: 100%;
  font-size: 13px;
  /deep/ th {
    background: #f7f8fa !important;
    color: #829199;
    font-weight: 500;
    font-size: 13px;
  }
  /deep/ td {
    color: #525f66;
    font-size: 13px;
  }
}
.contractManagementExpireWarn {
  margin-top: 4px;
  font-size: 12px;
  color: #f5222d;
  line-height: 1.4;
}
.contractManagementAction {
  color: #00c2b8;
  text-decoration: none;
  margin-right: 16px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &--solo {
    margin-right: 0;
  }
  &:last-child {
    margin-right: 0;
  }
}
</style>
